import React from "react";
import { useViewport, NewRadioGroup, OverlayProvider } from "@bca/ui-library";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  StyledFinalDetails,
  StyledContent,
  StyledTitle,
  StyledSection,
  StyledInterestsGroup,
  StyledRadioGroup,
  StyledRadioLabel,
  StyledRequired,
  StyledInterestsLabel,
  StyledInterestsButtonsGroup,
  StyledInterestButton,
  StyledOtherInformGroup,
  StyledOtherInformLabel,
  StyledCheckboxGroup,
  StyledCheckbox,
  StyledUploadDocumentsGroup,
  StyledUploadDocumentsTtile,
  StyledUploadDocumentsParagraph,
  StyledUploadDocumentsLabel,
  StyledUploadButton,
  StyledTCGroup,
  StyledTCCheckbox,
  StyledTCLabel,
  StyledSignGroup,
  StyledSignLabel,
  StyledSignButton,
  StyledUploadGroup,
  StyledUploadButtonsGroup,
  StyledUploadButtonContainer,
  StyledUploadDocumentsInfo,
  StyledUploadDocumentsInfoText,
  StyledUploadDocumentsSpecificText,
  StyledUploadDocumentsSpecificCheckboxContainer,
  StyledSpecificForCountryRadioGroup,
  StyledSpecificForCountryRadioLabel,
  StyledRadioButtonGroup,
  StyledInput,
  StyledInputGroup,
} from "./FinalDetails.styled";
import Button from "../../components/Button/Button";
import WebStepper from "../../components/WebStepper/WebStepper";
import { getFinalDetails } from "../../reducers/finalDetailsSlice";
import {
  changeCountryInterestsCheckbox,
  changeInformedByEmailCheckbox,
  changeInformedByOtherWaysCheckbox,
  changeTcAndPrivacy,
  changeElectronicSignature,
  changeUploadDocuments,
  changeSpecificCheckbox,
  changeChamberOfCommerceNumber,
} from "../../reducers/formSlice";
import SignatureModal from "../../components/SignatureModal/SignatureModal";
import SocialSelectModal from "../../components/SocialSelectModal/SocialSelectModal";
import fetchMobileStepper from "../../utils/fetchMobileStepper";
import CircularStepper from "../../components/CircularStepper/CircularStepper";
import {
  CONTACT_INFORMATION_PATH,
  FINAL_DETAILS_PATH,
  HOME_PATH,
} from "../../constants/paths";
import { setShowServerErrorMessage } from "../../reducers/errorManagementSlice";
import {
  initializeDigitalData,
  updateInitializedDigitalDataForFirstPage,
} from "../../hooks/scriptsHooks";

const FinalDetails = () => {
  const { page_content, loading } = useSelector(
    (state) => state.finalDetailsScreen
  );

  const { country_name: countrySelected, ...formState } = useSelector(
    (state) => state.form
  );

  const navigate = useNavigate();

  if (!countrySelected) {
    navigate(HOME_PATH, { replace: true });
  }

  const { value: languageCode, country_iso3 } = useSelector(
    (state) => state.languageOptions
  );

  const { showLoadingScreen } = useSelector((state) => state.loadingScreen);

  const [showModal, setShowModal] = React.useState(false);

  const [showSignModal, setShowSignModal] = React.useState(false);

  const [
    isChamberOfCommerceNumberCorrect,
    setIsChamberOfCommerceNumberCorrect,
  ] = React.useState(true);

  const [chamberOfCommerceValue, setChamberOfCommerceValue] =
    React.useState("");

  const [mobileStepper, setMobileStepper] = React.useState();

  const dispatch = useDispatch();

  const [continueButtonDisabled, setContinueButtonDisabled] =
    React.useState(true);

  const refs = React.useRef([]);

  let value = 0;

  const acceptedFilesType = page_content?.supported_file_extensions?.extensions;

  const fileCount = page_content?.documents_list?.map((document) => {
    const newArray = [...Array(document.total_amount)].map(() => {
      const newValue = value;
      value += 1;

      return newValue;
    });

    return newArray;
  });
  const vp = useViewport();
  const isMobile = vp.lessThan.md;

  const handleSave = (url) => {
    dispatch(changeElectronicSignature(url));
    setShowSignModal(false);
  };

  const handleClick = (ref, isPicked, index = 0) => {
    if (isPicked) {
      dispatch(
        changeUploadDocuments({
          index,
          data: {
            isPicked: false,
            file: null,
            filename: null,
            document_type: null,
          },
        })
      );
    } else {
      ref.click();
    }
  };

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleChange = async (event, document, index = 0) => {
    const fileUploaded = event.target.files[0];
    const documentsSupportedList = acceptedFilesType.map((file) =>
      file.replace(".", "")
    );

    if (documentsSupportedList.includes(fileUploaded.type.split("/")[1])) {
      const base64 = await convertBase64(fileUploaded);

      if (window.digitalData) {
        window.digitalData.push({
          event: {
            name: "click",
            category: "document",
            label: document.analytics_label,
            action: "upload",
            type: fileUploaded.type,
          },
        });
      }

      dispatch(
        changeUploadDocuments({
          index,
          data: {
            isPicked: true,
            file: base64,
            filename: fileUploaded.name,
            document_type: document?.document_type,
          },
        })
      );
    }
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);

    dispatch(
      getFinalDetails(
        countrySelected,
        languageCode,
        formState?.company_type?.value
      )
    );

    const getResult = async () => {
      const result = await fetchMobileStepper(languageCode, setMobileStepper);
      dispatch(setShowServerErrorMessage(result.responseStatus));
    };
    getResult();
  }, [dispatch, countrySelected, languageCode, formState?.company_type?.value]);

  React.useEffect(() => {
    const documentsVerify = () => {
      const verify = page_content?.documents_list?.map((document, index) => {
        return [...Array(document.total_amount)].map((_, i) => {
          return document.required
            ? formState?.uploaded_documents?.value[fileCount[index][i]]
                ?.isPicked || false
            : true;
        });
      });

      const verifyArray = page_content?.documents_list?.map(
        (document, index) => {
          let isVerified = true;
          if (document.required) {
            isVerified =
              verify[index].filter((item) => item === true).length >=
              document.required_amount;
          }

          return isVerified;
        }
      );

      return verifyArray?.every((item) => item === true);
    };

    if (
      (page_content?.country_interests_checkbox?.required === true
        ? formState?.country_interests_checkbox?.value?.some(
            (interest) => interest.checked === true
          )
        : true) &&
      (page_content?.informed_by_email_checkbox?.required === true
        ? !!formState?.informed_by_email_checkbox.value
        : true) &&
      (page_content?.informed_by_other_ways_checkbox?.required === true
        ? formState?.informed_by_other_ways_checkbox?.value?.some(
            (interest) => interest.checked === true
          )
        : true) &&
      (page_content?.tc_and_privacy?.required === true
        ? formState.accept_conditions.value
        : true) &&
      documentsVerify() &&
      !!formState?.electronic_signature.file &&
      (page_content?.chamber_of_commerce_number
        ? isChamberOfCommerceNumberCorrect
        : true)
    ) {
      setContinueButtonDisabled(false);
    } else {
      setContinueButtonDisabled(true);
    }
  }, [
    page_content?.country_interests_checkbox?.required,
    page_content?.documents_list,
    page_content?.informed_by_email_checkbox?.required,
    page_content?.informed_by_other_ways_checkbox?.required,
    page_content?.tc_and_privacy?.required,
    fileCount,
    formState?.country_interests_checkbox?.value,
    formState?.informed_by_email_checkbox?.value,
    formState?.informed_by_other_ways_checkbox?.value,
    formState?.accept_conditions?.value,
    formState?.electronic_signature?.file,
    formState?.uploaded_documents?.value,
    page_content?.chamber_of_commerce_number,
    isChamberOfCommerceNumberCorrect,
  ]);

  React.useEffect(() => {
    if (window.digitalData) {
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "4",
          stepName: "step 4 final details",
        },
        pageInfo: {
          pageTitle: "Final details",
          pagePath: `/${FINAL_DETAILS_PATH}`,
          previousPagePath: `/${CONTACT_INFORMATION_PATH}`,
        },
      });
    } else {
      initializeDigitalData();
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "4",
          stepName: "step 4 final details",
        },
        pageInfo: {
          pageTitle: "Final details",
          pagePath: `/${FINAL_DETAILS_PATH}`,
          previousPagePath: `/${CONTACT_INFORMATION_PATH}`,
        },
        site: {
          brandCode: country_iso3,
          brandName: "bca europe",
          language: languageCode,
          platformType: "cms",
          country: languageCode,
        },
      });
    }
  }, [country_iso3, languageCode]);
  const markdown = `<div>

${page_content?.tc_and_privacy?.label}

</div>`;

  const maximumFileSize = page_content?.info_maximum_file_size?.split(/\s{2,}/);

  return (
    <OverlayProvider>
      <SignatureModal
        isMobile={isMobile}
        showSignModal={showSignModal}
        onClose={() => setShowSignModal(false)}
        onSave={handleSave}
        clearMessage={page_content?.signature_btn_clear?.label || ""}
        continueMessage={page_content?.signature_btn_continue?.label || ""}
      />
      {!loading && (
        <>
          <SocialSelectModal
            showModal={showModal && !showLoadingScreen}
            modalHandler={() => setShowModal(false)}
          />

          <StyledFinalDetails>
            <StyledContent>
              {!isMobile && (
                <>
                  <WebStepper activeStep={3} />
                  <StyledTitle variant="h1">{page_content?.header}</StyledTitle>
                </>
              )}
              {isMobile && (
                <CircularStepper
                  mobileStepper={mobileStepper}
                  activeStepIndex={3}
                />
              )}
              <StyledSection isMobile={isMobile}>
                <StyledInterestsGroup>
                  <StyledInterestsLabel>
                    {page_content?.country_interests_checkbox?.label}
                    {page_content?.country_interests_checkbox?.required && (
                      <StyledRequired>*</StyledRequired>
                    )}
                  </StyledInterestsLabel>
                  <StyledInterestsButtonsGroup isMobile={isMobile}>
                    {page_content?.country_interests_checkbox?.options?.map(
                      (interest, index) => (
                        <StyledInterestButton
                          data-cy="final-details-interest-button"
                          key={interest?.label}
                          text={interest?.label}
                          variant={
                            formState?.country_interests_checkbox?.value[index]
                              ?.checked
                              ? "primary"
                              : "secondary"
                          }
                          onClick={() =>
                            dispatch(
                              changeCountryInterestsCheckbox(interest?.value)
                            )
                          }
                        />
                      )
                    )}
                  </StyledInterestsButtonsGroup>
                </StyledInterestsGroup>

                <StyledRadioGroup isMobile={isMobile}>
                  <StyledRadioLabel isMobile={isMobile}>
                    {page_content?.informed_by_email_checkbox?.label}
                    {page_content?.informed_by_email_checkbox?.required && (
                      <StyledRequired>*</StyledRequired>
                    )}
                  </StyledRadioLabel>
                  <StyledRadioButtonGroup
                    data-cy="final-details-radio-button-group"
                    onChange={(event) =>
                      dispatch(
                        changeInformedByEmailCheckbox(event.target.value)
                      )
                    }
                    name="email-inform"
                    value={formState?.informed_by_email_checkbox?.value}
                  >
                    <NewRadioGroup.Button
                      data-cy="final-details-radio-button-yes"
                      value={
                        page_content?.informed_by_email_checkbox?.options[0]
                          ?.value
                      }
                      label={
                        page_content?.informed_by_email_checkbox?.options[0]
                          ?.label
                      }
                    />
                    <NewRadioGroup.Button
                      data-cy="final-details-radio-button-no"
                      value={
                        page_content?.informed_by_email_checkbox?.options[1]
                          ?.value
                      }
                      label={
                        page_content?.informed_by_email_checkbox?.options[1]
                          ?.label
                      }
                    />
                  </StyledRadioButtonGroup>
                </StyledRadioGroup>

                <StyledOtherInformGroup isMobile={isMobile}>
                  <StyledOtherInformLabel>
                    {page_content?.informed_by_other_ways_checkbox?.label}
                    {page_content?.informed_by_other_ways_checkbox
                      ?.required && <StyledRequired>*</StyledRequired>}
                  </StyledOtherInformLabel>
                  <StyledCheckboxGroup isMobile={isMobile}>
                    {formState?.informed_by_other_ways_checkbox?.value?.map(
                      (checkbox, index) => (
                        <StyledCheckbox
                          data-cy="final-details-informed-by-other-ways-checkbox"
                          key={checkbox?.label}
                          checked={
                            formState?.informed_by_other_ways_checkbox?.value[
                              index
                            ]?.checked
                          }
                          onChange={() =>
                            dispatch(
                              changeInformedByOtherWaysCheckbox(checkbox.value)
                            )
                          }
                          label={checkbox?.label}
                        />
                      )
                    )}
                  </StyledCheckboxGroup>
                </StyledOtherInformGroup>

                {page_content?.chamber_of_commerce_number && (
                  <StyledInputGroup isMobile={isMobile}>
                    <StyledInput
                      data-cy="chamber-of-commerce-number"
                      state={
                        isChamberOfCommerceNumberCorrect ? "default" : "error"
                      }
                      message={
                        isChamberOfCommerceNumberCorrect
                          ? ""
                          : page_content?.chamber_of_commerce_number_error_message
                      }
                      colorOn="bg_white"
                      label={page_content?.chamber_of_commerce_number?.label}
                      required={
                        page_content?.chamber_of_commerce_number?.required
                      }
                      value={chamberOfCommerceValue}
                      onChange={(e) => {
                        let regex;
                        setChamberOfCommerceValue(e.target.value);

                        if (
                          page_content?.chamber_of_commerce_number_validator
                        ) {
                          regex = new RegExp(
                            page_content?.chamber_of_commerce_number_validator
                          );
                        }

                        if (e.target.value !== "") {
                          setIsChamberOfCommerceNumberCorrect(
                            regex.test(e.target.value)
                          );
                        } else {
                          setIsChamberOfCommerceNumberCorrect(false);
                        }
                      }}
                      onBlur={() =>
                        dispatch(
                          changeChamberOfCommerceNumber(chamberOfCommerceValue)
                        )
                      }
                    />
                  </StyledInputGroup>
                )}

                <StyledUploadDocumentsGroup isMobile={isMobile}>
                  <StyledUploadDocumentsTtile isMobile={isMobile}>
                    {page_content?.upload_documents_label}
                  </StyledUploadDocumentsTtile>

                  {maximumFileSize?.length &&
                    maximumFileSize.map((item) => (
                      <StyledUploadDocumentsParagraph
                        isMobile={isMobile}
                        key={item}
                      >
                        {item}
                      </StyledUploadDocumentsParagraph>
                    ))}

                  <StyledUploadDocumentsParagraph isMobile={isMobile}>
                    {page_content?.supported_file_extensions.message}
                  </StyledUploadDocumentsParagraph>

                  <StyledUploadDocumentsParagraph isMobile={isMobile}>
                    {page_content?.supported_file_extensions.message}
                  </StyledUploadDocumentsParagraph>

                  {page_content?.supported_file_extensions?.list_of_text_fields?.map(
                    (file) => (
                      <StyledUploadDocumentsParagraph
                        key={file.item_text}
                        isMobile={isMobile}
                      >
                        {file.item_text}
                      </StyledUploadDocumentsParagraph>
                    )
                  )}

                  {(countrySelected === "SE" || countrySelected === "NL") && (
                    <StyledUploadDocumentsSpecificCheckboxContainer>
                      <StyledUploadDocumentsSpecificText>
                        {page_content?.specific_for_country?.text}
                      </StyledUploadDocumentsSpecificText>
                    </StyledUploadDocumentsSpecificCheckboxContainer>
                  )}

                  {countrySelected === "PT" && (
                    <StyledUploadDocumentsSpecificCheckboxContainer>
                      <StyledSpecificForCountryRadioGroup isMobile={isMobile}>
                        <StyledSpecificForCountryRadioLabel isMobile={isMobile}>
                          {page_content?.specific_for_country?.text}
                          <StyledRequired>*</StyledRequired>
                        </StyledSpecificForCountryRadioLabel>
                        <StyledRadioButtonGroup
                          data-cy="final-details-specific-for-country-radio-button-group"
                          onChange={(event) => {
                            dispatch(
                              changeSpecificCheckbox(event.target.value)
                            );
                          }}
                          name="specific_for_country"
                          value={formState?.specific_checkbox?.value}
                        >
                          <NewRadioGroup.Button
                            data-cy="final-details-specific-for-country-radio-button-yes"
                            value={
                              page_content?.specific_for_country?.checkbox
                                ?.options[0]?.value
                            }
                            label={
                              page_content?.specific_for_country?.checkbox
                                ?.options[0]?.label
                            }
                          />
                          <NewRadioGroup.Button
                            data-cy="final-details-specific-for-country-radio-button-no"
                            value={
                              page_content?.specific_for_country?.checkbox
                                ?.options[1]?.value
                            }
                            label={
                              page_content?.specific_for_country?.checkbox
                                ?.options[1]?.label
                            }
                          />
                        </StyledRadioButtonGroup>
                      </StyledSpecificForCountryRadioGroup>
                    </StyledUploadDocumentsSpecificCheckboxContainer>
                  )}

                  {page_content?.documents_list?.map((document, index) => {
                    if (document?.required_user_consent) {
                      if (formState?.specific_checkbox?.value === "yes") {
                        return (
                          <StyledUploadGroup
                            key={document.title}
                            isMobile={isMobile}
                          >
                            <StyledUploadDocumentsLabel>
                              {document.title}
                              {document.required && (
                                <StyledRequired>*</StyledRequired>
                              )}
                            </StyledUploadDocumentsLabel>

                            {document.information && (
                              <StyledUploadDocumentsInfo>
                                <StyledUploadDocumentsInfoText>
                                  {document.information}
                                </StyledUploadDocumentsInfoText>
                              </StyledUploadDocumentsInfo>
                            )}
                            <StyledUploadButtonContainer isMobile={isMobile}>
                              {[...Array(document.total_amount)].map((_, i) => (
                                <StyledUploadButtonsGroup
                                  isMobile={isMobile}
                                  key={`${document.title}${fileCount[index][i]}`}
                                >
                                  <StyledUploadButton
                                    data-cy="final-details-upload-button"
                                    onClick={() =>
                                      handleClick(
                                        refs.current[fileCount[index][i]],
                                        formState.uploaded_documents.value[
                                          fileCount[index][i]
                                        ]?.isPicked,
                                        fileCount[index][i]
                                      )
                                    }
                                    text={
                                      formState?.uploaded_documents?.value[
                                        fileCount[index][i]
                                      ]?.filename
                                        ? `${
                                            formState?.uploaded_documents
                                              ?.value[fileCount[index][i]]
                                              ?.filename.length > 15
                                              ? `${formState?.uploaded_documents?.value[
                                                  fileCount[index][i]
                                                ]?.filename.slice(0, 15)}...`
                                              : formState?.uploaded_documents
                                                  ?.value[fileCount[index][i]]
                                                  ?.filename
                                          }`
                                        : page_content?.upload_button_text
                                    }
                                    icon={
                                      formState?.uploaded_documents?.value[
                                        fileCount[index][i]
                                      ]?.isPicked
                                        ? "delete"
                                        : "plus"
                                    }
                                    variant={
                                      formState?.uploaded_documents?.value[
                                        fileCount[index][i]
                                      ]?.isPicked
                                        ? "primary"
                                        : "secondary"
                                    }
                                  />
                                  <input
                                    data-cy="final-details-upload-input"
                                    type="file"
                                    accept={acceptedFilesType.join(", ")}
                                    // eslint-disable-next-line no-return-assign
                                    ref={(el) =>
                                      (refs.current[fileCount[index][i]] = el)
                                    }
                                    onChange={(event) =>
                                      handleChange(
                                        event,
                                        document,
                                        fileCount[index][i]
                                      )
                                    }
                                    onClick={(event) => {
                                      event.target.value = null;
                                    }}
                                    hidden
                                  />
                                </StyledUploadButtonsGroup>
                              ))}
                            </StyledUploadButtonContainer>
                          </StyledUploadGroup>
                        );
                      }
                      return null;
                    }
                    return (
                      <StyledUploadGroup
                        key={document.title}
                        isMobile={isMobile}
                      >
                        <StyledUploadDocumentsLabel>
                          {document.title}
                          {document.required && (
                            <StyledRequired>*</StyledRequired>
                          )}
                        </StyledUploadDocumentsLabel>

                        {document.information && (
                          <StyledUploadDocumentsInfo>
                            <StyledUploadDocumentsInfoText>
                              {document.information}
                            </StyledUploadDocumentsInfoText>
                          </StyledUploadDocumentsInfo>
                        )}
                        <StyledUploadButtonContainer isMobile={isMobile}>
                          {[...Array(document.total_amount)].map((_, i) => (
                            <StyledUploadButtonsGroup
                              isMobile={isMobile}
                              key={`${document.title}${fileCount[index][i]}`}
                            >
                              <StyledUploadButton
                                data-cy="final-details-upload-button"
                                onClick={() =>
                                  handleClick(
                                    refs.current[fileCount[index][i]],
                                    formState.uploaded_documents.value[
                                      fileCount[index][i]
                                    ]?.isPicked,
                                    fileCount[index][i]
                                  )
                                }
                                text={
                                  formState?.uploaded_documents?.value[
                                    fileCount[index][i]
                                  ]?.filename
                                    ? `${
                                        formState?.uploaded_documents?.value[
                                          fileCount[index][i]
                                        ]?.filename?.length > 15
                                          ? `${formState?.uploaded_documents?.value[
                                              fileCount[index][i]
                                            ]?.filename.slice(0, 15)}...`
                                          : formState?.uploaded_documents
                                              ?.value[fileCount[index][i]]
                                              ?.filename
                                      }`
                                    : page_content?.upload_button_text
                                }
                                icon={
                                  formState?.uploaded_documents?.value[
                                    fileCount[index][i]
                                  ]?.isPicked
                                    ? "delete"
                                    : "plus"
                                }
                                variant={
                                  formState?.uploaded_documents?.value[
                                    fileCount[index][i]
                                  ]?.isPicked
                                    ? "primary"
                                    : "secondary"
                                }
                              />
                              <input
                                data-cy="final-details-upload-input"
                                type="file"
                                accept={acceptedFilesType.join(", ")}
                                // eslint-disable-next-line no-return-assign
                                ref={(el) =>
                                  (refs.current[fileCount[index][i]] = el)
                                }
                                onChange={(event) =>
                                  handleChange(
                                    event,
                                    document,
                                    fileCount[index][i]
                                  )
                                }
                                onClick={(event) => {
                                  event.target.value = null;
                                }}
                                hidden
                              />
                            </StyledUploadButtonsGroup>
                          ))}
                        </StyledUploadButtonContainer>
                      </StyledUploadGroup>
                    );
                  })}

                  <StyledTCGroup isMobile={isMobile}>
                    <StyledTCCheckbox
                      data-cy="final-details-tc-checkbox"
                      checked={formState?.accept_conditions?.value}
                      onChange={(checked) =>
                        dispatch(
                          changeTcAndPrivacy(checked.currentTarget.checked)
                        )
                      }
                    />
                    <StyledTCLabel>
                      <ReactMarkdown
                        rehypePlugins={[rehypeRaw]}
                        remarkPlugins={[remarkGfm]}
                      >
                        {markdown}
                      </ReactMarkdown>
                    </StyledTCLabel>
                  </StyledTCGroup>
                  <StyledSignGroup>
                    <StyledSignLabel>
                      {page_content?.signature_message}
                      <StyledRequired>*</StyledRequired>
                    </StyledSignLabel>
                  </StyledSignGroup>
                </StyledUploadDocumentsGroup>
                <StyledSignButton
                  data-cy="final-details-sign-button"
                  text={page_content?.signature_btn?.label}
                  onClick={() => setShowSignModal(true)}
                />
              </StyledSection>
            </StyledContent>
            <Button
              backButtonText={page_content?.back_button?.label}
              backLink="#"
              backButtonDisabled={page_content?.back_button?.disable}
              continueButtonText={page_content?.submit_button?.label}
              nextLink="#"
              nextButtonDisabled={continueButtonDisabled}
              onNextClick={() => setShowModal(true)}
              onBackClick={() => navigate(-1)}
              isMobile={vp.lessThan.md}
            />
          </StyledFinalDetails>
        </>
      )}
    </OverlayProvider>
  );
};

export default FinalDetails;
